// UpgradeTierModal.hook.ts owns the data + state machine for the Lead Agent
// self-serve tier-UPGRADE modal (C5 / RET-1304, RET-1614). Adapted from
// ReactivateLeadAgentModal.hook.ts: same preview→confirm→3DS shape, but it
// speaks the A8 tier-change endpoints and offers only HIGHER tiers. It is
// deliberately NOT a flag on ConfirmTierModal / ReactivateLeadAgentModal —
// upgrading is its own action/endpoints (docs/solutions/patterns/
// reactivation-is-not-change-plan-per-intent-semantics.md).
//
// Responsibilities:
//   - fetch the tier-change PREVIEW per selected tier (AbortController, fails
//     CLOSED so confirm stays disabled + retry on any error),
//   - perform the tier-change POST (single in-flight, idempotent prorationDate),
//   - drive a view machine over the A8 status branches, and
//   - run the 3DS step CLIENT-SIDE only (stripe.confirmCardPayment) — NEVER the
//     generic /tiers/confirm-3d-secure endpoint (it cancels the sub on a
//     declined 3DS; verified in A11). Success relies on the Stripe webhook.
//
// Unlike ReactivateLeadAgentModal there is NO renewal projection here: the A8
// preview IS the amount charged now (the immediate prorated delta), which is the
// only number this modal shows (RET-1304).
//
// Three race windows cross an await (preview, POST, confirmCardPayment); after
// each, bail if the active workspace changed (workspaceId is a prop, captured
// into a ref kept fresh each render).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { useStripe } from '@stripe/react-stripe-js';
import { LeadAgentAcceptanceService } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.service';
import {
  resolveSuggestedUpgradeTier,
  resolveUpgradeableTiers,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import type {
  tLeadAgentSelectableTier,
  tLeadAgentTier,
  tLeadAgentTierChangePreview,
  tLeadAgentTierChangeResult,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { extractPaymentIntentData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import type { tRenewalProjectionWireData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';

/**
 * View machine. `confirm` is the preview + tier-selection step; `processing` is
 * the in-flight POST / 3DS; `blocked` is a non-retryable business rule (4xx);
 * `error` is retryable (decline, transient 424, local/unknown).
 */
export type tUpgradeView =
  | { status: 'confirm' }
  | { status: 'processing' }
  | { status: 'success'; result: tLeadAgentTierChangeResult }
  | {
      status: 'error';
      /** Inline message (decline reason / generic), surfaced in the modal. */
      message: string | null;
    }
  | {
      status: 'blocked';
      /** Backend `{code, message}` for the business-rule block. */
      code: string | null;
      message: string | null;
    };

export type tUpgradePreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: tLeadAgentTierChangePreview };

/**
 * Renewal-projection state (A6). INFORMATIONAL ONLY: it feeds the "Tu próxima
 * renovación" breakdown and the tier card's recurring price, and it must never
 * gate the charge — see the fail-OPEN note on the fetch effect.
 */
export type tUpgradeProjectionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: tRenewalProjectionWireData };

export type tUseUpgradeTierModalArgs = {
  workspaceId: string;
  /** Current tier — the upgrade-only set is derived from it. */
  currentTier: tLeadAgentTier | null;
  /** Live consumption of the current period (0-100), for the tier suggestion. */
  currentCapacityPct: number | null;
  /** ISO end of the current cap period, for the tier suggestion. */
  capacityResetDate: string | null;
};

export type tUseUpgradeTierModal = {
  view: tUpgradeView;
  preview: tUpgradePreviewState;
  /**
   * Renewal projection for the selected tier. Fails OPEN: on `error` the modal
   * hides the renewal breakdown and the card price but the charge stays live.
   */
  projection: tUpgradeProjectionState;
  /** The higher tiers on offer (ascending). Empty ⇒ nothing to upgrade to. */
  upgradeableTiers: tLeadAgentSelectableTier[];
  /**
   * The tier whose cap actually survives the period at the current pace — the one
   * pre-selected and shown alone, with the rest behind "ver otras opciones".
   * Null when there is nothing to upgrade to.
   */
  suggestedTier: tLeadAgentSelectableTier | null;
  selectedTier: tLeadAgentSelectableTier;
  /** Confirm is enabled only when a preview is ready and we are not in flight. */
  confirmDisabled: boolean;
  selectTier: (tier: tLeadAgentSelectableTier) => void;
  /** Performs the tier-change POST (+ 3DS when required). */
  confirm: () => void;
  /** Retries the preview (preview error) or the whole confirm (charge error). */
  retry: () => void;
};

function isCanceled(error: unknown): boolean {
  return (error as AxiosError | undefined)?.code === 'ERR_CANCELED';
}

type tBffErrorBody = { code?: unknown; message?: unknown };

/** Reads the bff `{code, message}` body off an Axios 4xx, both optional. */
function readBffError(error: unknown): {
  code: string | null;
  message: string | null;
} {
  const data = (error as AxiosError<tBffErrorBody> | undefined)?.response?.data;
  const code = typeof data?.code === 'string' ? data.code : null;
  const message = typeof data?.message === 'string' ? data.message : null;
  return { code, message };
}

function statusOf(error: unknown): number | undefined {
  return (error as AxiosError | undefined)?.response?.status;
}

/**
 * Maps a thrown tier-change error to a terminal view. 4xx (e.g. 422
 * not-an-upgrade) are business-rule BLOCKS (not retryable); 424 is a transient
 * comms fault (retryable error); everything else (5xx, local/unknown) is a
 * defensive retryable error.
 */
function viewForThrownError(error: unknown): tUpgradeView {
  const status = statusOf(error);
  if (status === 424) {
    return { status: 'error', message: null };
  }
  if (status !== undefined && status >= 400 && status < 500) {
    const { code, message } = readBffError(error);
    return { status: 'blocked', code, message };
  }
  return { status: 'error', message: null };
}

export function useUpgradeTierModal(
  args: tUseUpgradeTierModalArgs,
): tUseUpgradeTierModal {
  const { workspaceId, currentTier, currentCapacityPct, capacityResetDate } =
    args;
  const stripe = useStripe();

  const upgradeableTiers = useMemo(
    () => resolveUpgradeableTiers(currentTier),
    [currentTier],
  );
  const hasUpgrade = upgradeableTiers.length > 0;

  /**
   * The recommendation is derived from the BURN RATE, not from the tier ordinal:
   * the cheapest upgrade that actually lasts the rest of the period. See
   * `resolveSuggestedUpgradeTier` for the fallbacks.
   */
  const suggestedTier = useMemo(
    () =>
      resolveSuggestedUpgradeTier({
        currentTier,
        capacityPct: currentCapacityPct,
        resetDate: capacityResetDate,
      }),
    [currentTier, currentCapacityPct, capacityResetDate],
  );

  // When there is no upgrade the value is a harmless placeholder — every
  // fetch/confirm path is gated on `hasUpgrade`, and the component renders an
  // empty state instead.
  const defaultTier = suggestedTier ?? upgradeableTiers[0] ?? 'SCALE';

  const [selectedTier, setSelectedTier] =
    useState<tLeadAgentSelectableTier>(defaultTier);
  const [preview, setPreview] = useState<tUpgradePreviewState>({
    status: 'idle',
  });
  const [view, setView] = useState<tUpgradeView>({ status: 'confirm' });
  const [projection, setProjection] = useState<tUpgradeProjectionState>({
    status: 'idle',
  });
  // Bumped on preview retry to re-run the preview effect without changing tier.
  const [previewNonce, setPreviewNonce] = useState(0);

  // Blocks the suggestion from overriding an explicit pick: usage props can
  // arrive late (provider still loading), so the suggestion may change AFTER
  // mount — but never over the user's choice
  // (feedback_useref_to_block_autoinit_after_user_picked).
  const hasUserPickedRef = useRef(false);

  const abortRef = useRef<AbortController | null>(null);
  // Separate controller from the preview's: the two fetches are independent and
  // aborting one must never cancel the other (the projection is allowed to fail).
  const projectionAbortRef = useRef<AbortController | null>(null);
  // Unmount guard: the confirm flow crosses up to three awaits (POST + 3DS),
  // each followed by a setView. If the modal unmounts mid-flight, those
  // setStates would warn / no-op — bail on each instead.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Synchronous in-flight latch — `view==='processing'` is async React state, so
  // two same-tick clicks would both read the pre-update value and fire two
  // POSTs. This ref flips synchronously so the 2nd click bails.
  const inFlightRef = useRef(false);
  // Active workspace captured fresh each render — read after every await to bail
  // if the user switched workspaces mid-flight (3 race windows).
  const activeWorkspaceIdRef = useRef(workspaceId);
  activeWorkspaceIdRef.current = workspaceId;

  // Adopt the suggestion when it resolves (or changes) and the user has not
  // picked yet. Never while a charge is in flight or after a terminal success.
  useEffect(() => {
    if (hasUserPickedRef.current) return;
    if (view.status === 'processing' || view.status === 'success') return;
    if (suggestedTier && suggestedTier !== selectedTier) {
      setSelectedTier(suggestedTier);
    }
  }, [suggestedTier, selectedTier, view.status]);

  // Keep the selected tier valid if the current tier changes under us (e.g. a
  // background usageTier refetch) and the previous pick is no longer an upgrade.
  // Never fires while a charge is in flight (would thrash the previewed amount)
  // or after a terminal success (would wipe the success screen).
  useEffect(() => {
    if (view.status === 'processing' || view.status === 'success') return;
    if (!upgradeableTiers.includes(selectedTier)) {
      setSelectedTier(defaultTier);
      // A stale error/blocked message was raised for the OLD tier, which is no
      // longer selectable — reset to 'confirm' so it can't outlive the auto-swap
      // (state-consistency: visible message always matches the visible tier).
      if (view.status !== 'confirm') setView({ status: 'confirm' });
    }
  }, [upgradeableTiers, selectedTier, defaultTier, view.status]);

  // ---- Preview fetch (per tier / retry), fails CLOSED ----
  useEffect(() => {
    if (!hasUpgrade) {
      // Nothing to upgrade to → leave preview idle; the component shows the
      // empty state (there is no amount to fetch).
      return;
    }
    if (!workspaceId) {
      // Missing workspace id but there IS an upgrade to offer → fail CLOSED to
      // the preview-error + retry state (mirrors ReactivateLeadAgentModal),
      // never a silent dead confirm view with a permanently-disabled submit.
      setPreview({ status: 'error' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestWorkspaceId = workspaceId;

    setPreview({ status: 'loading' });

    LeadAgentAcceptanceService.getTierChangePreview(workspaceId, {
      tier: selectedTier,
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        // Workspace mid-await guard (race window 1): a switch during the fetch
        // must not apply a stale preview.
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setPreview({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (isCanceled(error) || controller.signal.aborted) return;
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setPreview({ status: 'error' });
      });

    return () => {
      controller.abort();
    };
  }, [workspaceId, hasUpgrade, selectedTier, previewNonce]);

  // ---- Renewal projection fetch (per tier), fails OPEN ----
  // Informational only: it must NEVER touch `preview` / `view` /
  // `confirmDisabled`, or a non-essential read would block a payment that was
  // otherwise possible — the documented fail-closed trap
  // (docs/solutions/patterns/reactivation-is-not-change-plan-per-intent-semantics.md).
  // On error the breakdown and the card price simply do not paint.
  useEffect(() => {
    if (!hasUpgrade) return;
    if (!workspaceId) {
      setProjection({ status: 'error' });
      return;
    }

    projectionAbortRef.current?.abort();
    const controller = new AbortController();
    projectionAbortRef.current = controller;
    const requestWorkspaceId = workspaceId;

    setProjection({ status: 'loading' });

    SubscriptionManagerService.getRenewalProjection(
      workspaceId,
      {
        tier: selectedTier,
        // POST_LAUNCH is the honest cohort here: an upgrade is a workspace that
        // already PAYS for the Lead Agent, never a presale acceptance. It also
        // keeps the backend from applying the presale date floor and from
        // computing a bridge first charge — neither of which this modal reads
        // (today's amount is the A8 preview), but the request should not claim a
        // cohort the workspace is not in.
        origin: 'POST_LAUNCH',
      },
      controller.signal,
    )
      .then((data) => {
        if (controller.signal.aborted) return;
        // Workspace mid-await guard: a switch during the fetch must not apply a
        // stale projection.
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setProjection({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        // Aborted on tier switch — keep the newer request's loading state.
        if (isCanceled(error) || controller.signal.aborted) return;
        if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
        setProjection({ status: 'error' });
      });

    return () => {
      controller.abort();
    };
  }, [workspaceId, hasUpgrade, selectedTier]);

  const selectTier = useCallback((tier: tLeadAgentSelectableTier) => {
    hasUserPickedRef.current = true;
    setSelectedTier(tier);
    // Switching tier returns to the confirm step (a prior decline/error for the
    // old tier no longer applies) and re-arms the preview via the tier dep.
    setView({ status: 'confirm' });
  }, []);

  const runConfirm = useCallback(async () => {
    // Synchronous double-submit guard.
    if (inFlightRef.current) return;
    if (preview.status !== 'ready') return;

    const requestWorkspaceId = activeWorkspaceIdRef.current;
    const tierToConfirm = selectedTier;
    // The previewed prorationDate MUST be echoed verbatim so the charge is
    // idempotent on retry (sub+price+prorationDate) and matches the shown amount.
    const prorationDate = preview.data.prorationDate;

    inFlightRef.current = true;
    setView({ status: 'processing' });

    try {
      const result = await LeadAgentAcceptanceService.changeTier(
        requestWorkspaceId,
        { tier: tierToConfirm, prorationDate },
      );
      // Unmount guard (race window 2): the modal is gone — nothing to update.
      if (!isMountedRef.current) {
        inFlightRef.current = false;
        return;
      }
      // Workspace mid-await guard (race window 2). The charge stays attributed
      // to the original workspace (money-correct); reset the view to 'confirm'
      // so the modal does NOT strand on the 'processing' spinner with every
      // control disabled — reachable when currentWorkspace flips via the
      // cross-tab useLocalStorageState sync mid-charge. The preview effect
      // re-arms for the new workspace (workspaceId is a dep).
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) {
        inFlightRef.current = false;
        setView({ status: 'confirm' });
        return;
      }

      if (result.status === 'ok') {
        setView({ status: 'success', result });
        return;
      }

      if (result.status === 'failed') {
        // 200 + failed = payment decline (retryable). Surface the message.
        setView({ status: 'error', message: result.message });
        return;
      }

      // requires_action: confirm the 3DS challenge CLIENT-SIDE. NEVER call a
      // backend confirm endpoint here (the generic /tiers/confirm-3d-secure
      // cancels the subscription on a declined 3DS — verified, forbidden).
      const clientSecret = result.paymentIntentClientSecret;
      if (!stripe || !clientSecret) {
        setView({ status: 'error', message: null });
        return;
      }

      const confirmation = await stripe.confirmCardPayment(clientSecret);
      // Unmount guard (race window 3).
      if (!isMountedRef.current) {
        inFlightRef.current = false;
        return;
      }
      // Workspace mid-await guard (race window 3). Same as window 2: reset to
      // 'confirm' so the modal never strands on the spinner after a mid-3DS
      // workspace switch.
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) {
        inFlightRef.current = false;
        setView({ status: 'confirm' });
        return;
      }

      const { status } = extractPaymentIntentData(confirmation);
      if (confirmation.error || status !== 'succeeded') {
        // Declined / abandoned 3DS — retryable. Surface the decline reason when
        // Stripe provides one; rely on the webhook for the succeeded case;
        // never confirm against the backend.
        setView({
          status: 'error',
          message: confirmation.error?.message ?? null,
        });
        return;
      }

      // 3DS succeeded: charge processing, webhook finalizes (raises the cap via
      // A2). Reuse the A8 result (carries the tier/invoiceId) for the success copy.
      setView({ status: 'success', result });
    } catch (error) {
      if (!isMountedRef.current) return;
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
      setView(viewForThrownError(error));
    } finally {
      inFlightRef.current = false;
    }
  }, [preview, selectedTier, stripe]);

  const confirm = useCallback(() => {
    void runConfirm();
  }, [runConfirm]);

  const retry = useCallback(() => {
    // From a preview error: re-fetch the preview. From a charge error: rewind to
    // the confirm step (the preview is still ready) so the user can resubmit
    // with the SAME prorationDate.
    if (preview.status === 'error' || preview.status === 'idle') {
      setPreview({ status: 'loading' });
      setPreviewNonce((n) => n + 1);
      setView({ status: 'confirm' });
      return;
    }
    setView({ status: 'confirm' });
  }, [preview.status]);

  const confirmDisabled =
    preview.status !== 'ready' || view.status === 'processing';

  return {
    view,
    preview,
    projection,
    upgradeableTiers,
    suggestedTier,
    selectedTier,
    confirmDisabled,
    selectTier,
    confirm,
    retry,
  };
}
